import { useState, useMemo, useEffect, useRef } from "react";
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import {
  T, SERIF, SANS, SECTIONS, ALL_Q, RESULT_NARRATIVES, NEXT_MOVES,
  tierFor, computeModel, encodeAnswers, decodeAnswers, shareUrl, copyToClipboard,
} from "./lib/model";
import { saveSubmission } from "./lib/ncb";
import LeadGate from "./components/LeadGate";
import Report from "./components/Report";

const { FiClock, FiShield, FiCheckCircle, FiInfo, FiArrowRight, FiArrowLeft, FiRefreshCw, FiAlertTriangle } = FiIcons;

const SAMPLE_ANSWERS = {
  "phish_sim": 3,      // Never
  "reporting_process": 3, // No process
  "mfa_enforcement": 2, // Admin access only
  "password_mgmt": 3,   // None
  "payment_verification": 3, // Informal
  "exec_impersonation": 2, // Usually follow orders
  "file_sharing": 2,    // Standard email
  "ai_governance": 2,   // No policy
  "offboarding_speed": 2, // Within a week
  "security_awareness": 3 // Rarely
};

const SAMPLE_LEAD = {
  name: "Alex Thompson",
  email: "alex@globalcyber.co",
  company: "Global Cyber Solutions",
  role: "VP of Operations",
  headcount: "101-500"
};

function initialAnswers() {
  try {
    const fromUrl = decodeAnswers(new URLSearchParams(window.location.search).get("a"));
    if (fromUrl && Object.keys(fromUrl).length > 0) return fromUrl;
  } catch { /* start fresh */ }
  return {};
}

export default function HumanRiskIndex({ onBackToHome, isSample = false }) {
  const [answers, setAnswers] = useState(isSample ? SAMPLE_ANSWERS : initialAnswers);
  const [lead, setLead] = useState(isSample ? SAMPLE_LEAD : null);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showReport, setShowReport] = useState(isSample);
  const [showGate, setShowGate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showExplanation, setShowExplanation] = useState(!isSample);
  
  const questionRefs = useRef({});

  const setAns = (qid, idx) => {
    setAnswers((a) => ({ ...a, [qid]: idx }));
    setCopied(false);
    setLinkCopied(false);

    const currentIndex = ALL_Q.findIndex(q => q.id === qid);
    if (currentIndex < ALL_Q.length - 1) {
      const nextQ = ALL_Q[currentIndex + 1];
      setTimeout(() => {
        const nextEl = questionRefs.current[nextQ.id];
        if (nextEl) {
          nextEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          nextEl.focus({ preventScroll: true });
        }
      }, 400);
    }
  };

  const reset = () => {
    if (window.confirm("Are you sure you want to reset the assessment? All progress will be lost.")) {
      setAnswers({});
      setCopied(false);
      setLinkCopied(false);
      setShowReport(false);
      setShowGate(false);
      setLead(null);
      const url = new URL(window.location.href);
      url.searchParams.delete("a");
      window.history.replaceState({}, "", url.toString());
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const model = useMemo(() => computeModel(answers), [answers]);
  const tier = model.tier;
  const complete = model.answered === ALL_Q.length;
  const progressPct = (model.answered / ALL_Q.length) * 100;

  const openReport = () => {
    if (lead) setShowReport(true);
    else setShowGate(true);
  };

  const unlock = async (leadInfo) => {
    setSaving(true);
    setLead(leadInfo);
    
    await saveSubmission({
      name: leadInfo.name,
      email: leadInfo.email,
      company: leadInfo.company,
      role: leadInfo.role,
      headcount: leadInfo.headcount,
      score: model.score, // Internal storage
      tier: tier?.name ?? null,
      status: model.identity.status,
      answered: model.answered,
      answers_encoded: encodeAnswers(answers),
      answers: JSON.stringify(answers),
      findings_count: model.findings.length,
      assessment_id: model.identity.id,
      methodology_version: model.identity.version,
      validation_status: model.identity.status,
      confidence_notice: model.confidenceNotice,
      executive_decisions: JSON.stringify(model.executiveDecisions),
      action_items: JSON.stringify(model.findings)
    });
    
    setSaving(false);
    setShowGate(false);
    setShowReport(true);
  };

  const copySummary = async () => {
    if (!tier) return;
    const lines = [
      "HUMAN RISK EXECUTIVE SUMMARY",
      "Human Risk Index (TM) / Power Plays by Eric Stavola",
      "",
      `Assessment ID: ${model.identity.id}`,
      `Risk Band: ${tier.name}`,
      `Executive Meaning: ${tier.meaning}`,
      `Confidence: ${model.confidenceNotice}`,
      `Status: ${model.identity.status} Risk Band`,
      "",
      "Strategic Dimension Profile:",
      ...model.dims.map((d) => {
        const val = d.pct === null ? "not assessed" : "Profiled";
        return `  ${d.name}: ${val}`;
      }),
      "",
      "Top Critical Findings:",
      ...(model.findings.length
        ? model.findings.slice(0, 3).map((f, i) => `  ${i + 1}. ${f.view}\n     Action: ${f.remedy}`)
        : ["  No major exposure triggered yet."]),
      "",
      "Strategic Next Move:",
      `  ${NEXT_MOVES[tier.name]}`,
    ];
    const success = await copyToClipboard(lines.join("\n"));
    setCopied(success);
  };

  const copyLink = async () => {
    const success = await copyToClipboard(shareUrl(answers));
    setLinkCopied(success);
  };

  if (showReport && tier) {
    return (
      <Report
        model={model}
        tier={tier}
        answers={answers}
        lead={lead}
        onBack={() => {
          if (isSample) onBackToHome();
          else setShowReport(false);
        }}
      />
    );
  }

  if (showExplanation) {
    return (
      <div style={{ background: T.bg, fontFamily: SANS, minHeight: "100vh" }} className="flex flex-col items-center justify-center p-6">
        <div className="max-w-3xl w-full bg-white p-8 md:p-12 rounded-[40px] shadow-2xl border border-gray-100">
           <div className="flex items-center gap-3 mb-8">
              <SafeIcon icon={FiInfo} className="w-8 h-8 text-blue-500" />
              <h2 style={{ fontFamily: SERIF }} className="text-3xl font-black text-[#0D2440]">Strategic Risk Model</h2>
           </div>
           <p className="text-lg text-gray-600 leading-relaxed font-medium">
             The Human Risk Index evaluates critical exposure categories to identify measurable business impact across the workforce layer.
           </p>
           <div className="mt-8 grid gap-4">
             {SECTIONS.map(s => (
               <div key={s.id} className="flex gap-4 items-start p-5 bg-gray-50 rounded-2xl border border-gray-100">
                 <div className="w-10 h-10 rounded-xl bg-[#0D2440] text-white flex flex-col items-center justify-center flex-shrink-0 mt-1">
                   <SafeIcon icon={FiShield} className="w-5 h-5" />
                 </div>
                 <div className="flex-1">
                   <div className="flex justify-between items-start">
                     <div className="font-black text-[#0D2440] text-lg">{s.name}</div>
                     <div className="text-[10px] font-black uppercase text-blue-600 bg-blue-50 px-2 py-0.5 rounded tracking-widest">{s.impact}</div>
                   </div>
                   <div className="text-xs text-gray-500 font-bold mt-1 leading-relaxed">
                     {s.narrative}
                   </div>
                 </div>
               </div>
             ))}
           </div>
           <button 
             onClick={() => setShowExplanation(false)}
             style={{ background: T.ink, color: "#FFFFFF" }}
             className="w-full mt-10 py-5 rounded-2xl text-lg font-black flex items-center justify-center gap-3 hover:brightness-110 transition-all shadow-xl active:scale-95"
           >
             Start Strategic Assessment
             <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
           </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: T.bg, fontFamily: SANS, minHeight: "100vh", color: T.ink }}>
      <div className="fixed top-0 left-0 right-0 z-50 h-2 bg-[#0D2440]/10" role="progressbar">
        <div 
          className="h-full bg-[#A4D82B] transition-all duration-500 ease-out shadow-[0_0_15px_rgba(164,216,43,0.4)]" 
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {showGate && <LeadGate onUnlock={unlock} onClose={() => setShowGate(false)} saving={saving} />}

      <header style={{ background: T.ink }} className="px-6 py-10 md:px-10 pt-12">
        <div className="max-w-6xl mx-auto flex flex-wrap items-end justify-between gap-6">
          <div className="flex-1 min-w-[300px]">
            <div className="flex justify-between items-start mb-6">
              <button onClick={onBackToHome} className="flex items-center gap-2 text-blue-200 text-sm font-bold hover:text-white transition-colors group">
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                Main Menu
              </button>
              
              <button onClick={reset} className="flex items-center gap-2 text-red-300 text-xs font-bold hover:text-red-400 transition-colors group">
                <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5 transition-transform group-hover:rotate-180 duration-500" />
                Reset Assessment
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              <img
                src="https://media-manager-c.questera.ai/greta-media/ecb16a7902f793d76e4d541ba9ce1c3ae6d96976df461599e59c7024cc2713617bff44ec4f8cbf96d32b4bb87bd2d993/images/aW1hZ2UvcG5n/07cb85c4268f1317cf05349009b1d943.png"
                alt="Logo"
                style={{ height: 40, width: "auto" }}
              />
              <div style={{ color: T.teal, letterSpacing: "0.22em", fontSize: 10, fontWeight: 700 }}>
                HUMAN RISK INDEX&trade;
              </div>
            </div>
            <h1 style={{ fontFamily: SERIF, fontSize: "clamp(24px, 3.5vw, 36px)", color: "#FFFFFF", lineHeight: 1.2 }} className="mt-4 font-black max-w-2xl">
              Strategic Risk Assessment
            </h1>
            
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6">
              <div className="flex items-center gap-2 text-blue-200 text-xs font-bold bg-white/5 px-3 py-1.5 rounded-full">
                <SafeIcon icon={FiClock} className="w-4 h-4 text-teal-400" />
                <span>3-Minute Pulse</span>
              </div>
              <div className="flex items-center gap-2 text-blue-200 text-xs font-bold bg-white/5 px-3 py-1.5 rounded-full">
                <SafeIcon icon={FiShield} className="w-4 h-4 text-lime-400" />
                <span>Behavior-First Model</span>
              </div>
              <div className="flex items-center gap-2 text-blue-200 text-xs font-bold bg-white/5 px-3 py-1.5 rounded-full">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-blue-400" />
                <span>Executive Interpretation</span>
              </div>
            </div>
          </div>
          <div className="text-right flex flex-col items-end gap-4">
            <div className="bg-white/5 p-6 rounded-[24px] border border-white/10 backdrop-blur-md">
              <div style={{ color: T.teal, fontSize: 10, letterSpacing: "0.2em", fontWeight: 800 }} className="mb-2 uppercase">Current Progress</div>
              <div className="flex items-baseline gap-1">
                <span style={{ fontFamily: SERIF, color: "#FFFFFF", fontSize: 32, fontWeight: 800 }}>
                  {model.answered}
                </span>
                <span style={{ color: "#C5D3E2", fontSize: 18, fontWeight: 700 }}> / {ALL_Q.length}</span>
              </div>
              <div className="w-40 h-2 bg-white/10 rounded-full mt-4 overflow-hidden">
                <div 
                  className="h-full bg-[#A4D82B] transition-all duration-300" 
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-16 grid gap-16 lg:grid-cols-[1fr_400px] items-start">
        <div className="space-y-16">
          {SECTIONS.map((s) => (
            <section key={s.id}>
              <div className="flex flex-col gap-2 mb-8 border-l-4 border-lime-400 pl-6">
                <div className="flex items-center gap-3">
                  <h2 style={{ fontFamily: SERIF, color: T.ink, fontSize: 24 }} className="font-black">{s.name}</h2>
                  <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded tracking-widest uppercase">{s.impact}</span>
                </div>
                <p className="text-sm text-gray-500 font-bold max-w-xl">{s.narrative}</p>
              </div>
              <div className="space-y-8">
                {s.questions.map((q) => {
                  const sel = answers[q.id];
                  return (
                    <div 
                      key={q.id} 
                      ref={el => questionRefs.current[q.id] = el}
                      tabIndex="-1"
                      style={{ background: T.paper, border: `1px solid ${T.rule}`, borderRadius: 24 }} 
                      className="p-8 transition-all hover:border-blue-400/40 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-600/5"
                    >
                      <div className="font-black mb-6 text-gray-900 text-lg leading-snug">{q.text}</div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {q.opts.map(([label], i) => {
                          const active = sel === i;
                          return (
                            <button
                              type="button"
                              key={i}
                              onClick={() => setAns(q.id, i)}
                              style={{
                                background: active ? T.ink : T.bg,
                                color: active ? "#FFFFFF" : T.slate,
                                border: `1px solid ${active ? T.ink : T.rule}`,
                                borderRadius: 16,
                                fontSize: 15,
                              }}
                              className={`px-6 py-4 font-black transition-all active:scale-[0.97] text-left relative overflow-hidden group`}
                            >
                              <div className="flex items-center justify-between relative z-10">
                                <span>{label}</span>
                                {active && <SafeIcon icon={FiIcons.FiCheck} className="text-lime-400 w-5 h-5 flex-shrink-0" />}
                              </div>
                              {!active && <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-300" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <aside className="lg:sticky lg:top-10 space-y-8">
          <div style={{ background: T.ink, borderRadius: 32, overflow: "hidden" }} className="shadow-2xl border border-white/5">
            <div className="p-8">
              <div style={{ color: T.lime, fontSize: 10, letterSpacing: "0.2em", fontWeight: 800 }}>EXECUTIVE RISK BAND</div>
              
              {tier && (
                <div className="mt-6 flex flex-col gap-4">
                  <div style={{ background: tier.color, color: (tier.color === T.gold || tier.color === T.limeDeep) ? T.ink : "#FFFFFF", borderRadius: 16, fontSize: 24, fontWeight: 900 }} className="px-6 py-6 text-center shadow-lg uppercase">
                    {tier.name} RISK
                  </div>
                  
                  <div className="flex items-center gap-2 text-teal-400 text-[10px] font-black uppercase tracking-widest bg-white/5 px-3 py-2 rounded-xl border border-white/10">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                    <span>{model.confidenceNotice} Profile</span>
                  </div>
                </div>
              )}

              {tier && (
                <div className="mt-8 space-y-4">
                  <p style={{ color: "#FFFFFF", fontSize: 16 }} className="leading-relaxed font-black">
                    Executive Meaning:
                  </p>
                  <p style={{ color: "#D1DCE8", fontSize: 14.5 }} className="leading-relaxed font-medium italic">
                    "{tier.meaning}"
                  </p>
                </div>
              )}

              <div className="mt-10 space-y-6">
                {model.dims.map((d) => (
                  <div key={d.name}>
                    <div className="flex justify-between items-center" style={{ fontSize: 11, color: "#C5D3E2" }}>
                      <span className="font-extrabold uppercase tracking-widest">{d.name}</span>
                      <span className="font-black text-white text-sm">{d.pct === null ? "–" : "Profiled"}</span>
                    </div>
                    <div style={{ background: "#1C4066", borderRadius: 999, height: 8 }} className="mt-3 overflow-hidden">
                      <div style={{ width: d.pct === null ? "0%" : `${Math.max(4, d.pct * 100)}%`, background: d.pct === null ? "transparent" : tierFor(Math.round(d.pct * 100)).color, borderRadius: 999, height: 8, transition: "all 600ms cubic-bezier(0.4, 0, 0.2, 1)" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: "rgba(0,0,0,0.2)", borderTop: "1px solid #1C4066" }} className="p-8">
              <div style={{ color: T.lime, fontSize: 10, letterSpacing: "0.2em", fontWeight: 800 }}>CRITICAL RISK FINDINGS</div>
              {model.findings.length === 0 ? (
                <p style={{ color: "#C5D3E2", fontSize: 14 }} className="mt-4 leading-relaxed font-medium italic">
                  {model.answered === 0 ? "As you answer, critical risk drivers will surface here." : "No critical behavioral exposures surfaced yet."}
                </p>
              ) : (
                <div className="mt-6 space-y-6">
                  <ul className="space-y-6">
                    {model.findings.slice(0, 3).map((f, i) => (
                      <li key={i} className="animate-in fade-in slide-in-from-right-4 duration-500">
                        <div className="flex gap-4">
                          <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <div style={{ color: "#FFFFFF", fontSize: 15, fontFamily: SERIF, lineHeight: 1.4, fontWeight: 700 }}>"{f.view}"</div>
                            <div style={{ color: T.lime, fontSize: 12, marginTop: 4, fontWeight: 800 }} className="uppercase tracking-wide">MOVE: {f.remedy}</div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={openReport}
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}
                    className="w-full mt-4 py-4 rounded-2xl text-xs font-black text-blue-200 hover:bg-white/10 transition-all active:scale-[0.98] uppercase tracking-widest"
                  >
                    Generate Executive Report &rarr;
                  </button>
                </div>
              )}
            </div>

            <div className="p-8" style={{ borderTop: "1px solid #1C4066" }}>
              <div style={{ color: "#FFFFFF", fontSize: 18, fontWeight: 900 }}>Strategic Next Move</div>
              {tier && (
                <div style={{ background: "#0D2440", border: "1px solid #234E7C", borderRadius: 20 }} className="p-6 mt-6 shadow-inner">
                  <div style={{ color: T.lime, fontSize: 10, letterSpacing: "0.15em", fontWeight: 800 }} className="uppercase">Priority Action</div>
                  <p style={{ color: "#FFFFFF", fontSize: 16 }} className="mt-3 leading-relaxed font-black">{NEXT_MOVES[tier.name]}</p>
                </div>
              )}

              <div className="flex flex-col gap-4 mt-8">
                <a href={`mailto:coach@ericstavola.com?subject=Human Risk Review&body=I would like to schedule a review of my Human Risk Index results.`} style={{ background: T.lime, color: T.ink, borderRadius: 16, fontWeight: 900, fontSize: 16 }} className="px-6 py-4 text-center transition-all hover:brightness-105 active:scale-[0.98] shadow-xl shadow-lime-900/20">
                  Schedule Human Risk Review
                </a>
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    type="button" 
                    onClick={copySummary} 
                    disabled={model.answered === 0} 
                    style={{ 
                      background: copied ? T.navy : "rgba(255,255,255,0.05)", 
                      color: "#FFFFFF", 
                      border: `1px solid ${copied ? T.navy : "rgba(255,255,255,0.15)"}`, 
                      borderRadius: 16, 
                      fontSize: 12 
                    }} 
                    className={`px-4 py-4 font-black transition-all ${model.answered !== 0 ? 'hover:bg-white/10 active:scale-[0.96]' : 'opacity-50'} uppercase tracking-widest`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {copied ? <SafeIcon icon={FiIcons.FiCheck} className="text-lime-400 w-4 h-4" /> : <SafeIcon icon={FiIcons.FiCopy} className="w-4 h-4" />}
                      <span>Summary</span>
                    </div>
                  </button>
                  <button 
                    type="button" 
                    onClick={copyLink} 
                    disabled={model.answered === 0} 
                    style={{ 
                      background: linkCopied ? T.navy : "rgba(255,255,255,0.05)", 
                      color: "#FFFFFF", 
                      border: `1px solid ${linkCopied ? T.navy : "rgba(255,255,255,0.15)"}`, 
                      borderRadius: 16, 
                      fontSize: 12 
                    }} 
                    className={`px-4 py-4 font-black transition-all ${model.answered !== 0 ? 'hover:bg-white/10 active:scale-[0.96]' : 'opacity-50'} uppercase tracking-widest`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {linkCopied ? <SafeIcon icon={FiIcons.FiCheck} className="text-lime-400 w-4 h-4" /> : <SafeIcon icon={FiIcons.FiLink} className="w-4 h-4" />}
                      <span>Share</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 space-y-6">
            <p className="text-xs text-gray-400 font-bold leading-relaxed italic">
              The Human Risk Index is a directional planning tool designed for executive teams. 
              It is not a penetration test or technical guarantee.
            </p>
            <div style={{ borderTop: `1px solid ${T.rule}` }} className="pt-6">
              <p style={{ color: T.ink, fontSize: 13, fontWeight: 900 }}>Human Risk Index&trade;</p>
              <p style={{ color: T.muted, fontSize: 11, fontWeight: 600 }} className="mt-1">Power Plays by Eric Stavola &middot; Intelligent Provider Services.</p>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}